/**
 * FeaturedCarousel Component
 * Wrapper around the generic Carousel for displaying featured rituals
 */

import { useEffect, useState } from "react"
import { RitualCard, type RitualCardProps } from "@/components/cards/ritualCard"
import { Carousel, type ResponsiveItemsConfig } from "@/components/carousel/Carousel"
import type { CarouselApi } from "@/components/ui/carousel"
import { useIsMobile } from "@/hooks/useIsMobile"
import { cn } from "@/lib"

export interface FeaturedCarouselProps {
  rituals: RitualCardProps[]
  itemsPerScreen?: ResponsiveItemsConfig // Optional responsive configuration
  className?: string
}

export function FeaturedCarousel({
  rituals,
  itemsPerScreen: _itemsPerScreen,
  className: _className,
}: FeaturedCarouselProps) {
  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const isMobile = useIsMobile()

  useEffect(() => {
    if (!api) return

    setCurrent(api.selectedScrollSnap())

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap())
    })
  }, [api])

  return (
    <div className="relative">
      <Carousel
        data={rituals}
        renderItem={ritual => <RitualCard {...ritual} />}
        itemWidth={isMobile ? "min(656px, 100%)" : "min(1200px, 100%)"}
        contentClassName="h-[260px] md:h-[600px]"
        setApi={setApi}
      />
      {rituals.length > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          {rituals.map((_, index) => (
            <button
              key={index}
              onClick={() => api?.scrollTo(index)}
              className={cn(
                "h-1 w-7.5 rounded-none transition-all",
                index === current
                  ? "bg-text-primary"
                  : "bg-text-primary/20 hover:bg-text-primary/40",
              )}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  )
}
